import styled from "styled-components"
import Footer from "./Footer"
import Navigation from "./Navigation"

import { useNavigate } from "react-router-dom"


const projects = [
    {
        title: "WildcatPortaPotti.com",
        description:
            "A local business site for a portable-toilet rental company serving Ohio, Kentucky, and Indiana. Built from the ground up in close collaboration with the owners to match how they actually run the business.",
        status: "public",
        url: "https://wildcatportapotti.com",
    },
    {
        title: "Spiraledge Ticketing System",
        description:
            "An internal ticketing tool built after noticing the existing system was outdated and hard to maintain. Employees submit tickets to a shared database, and IT staff triage and manage them from there.",
        status: "internal",
    },
    {
        title: "Spiraledge AI Monitoring System",
        description:
            "An in-house platform I now own the development and management of. It tracks how employees are using AI tools and layers in coaching, training, and usage insights on top of that data.",
        status: "internal",
    },
    {
        title: "cookconnor.com",
        description:
            "This site. Built with React and Node, styled with styled-components, and designed as a running record of the work above.",
        status: "current",
    },
]

const statusLabel = {
    internal: "This app is an internal built and is being utilized by the company. Please reach out for a demo",
    current: "You're looking at it",
    public: null,
}

const PortfolioPage = () => {
    
    const navigate = useNavigate()

    const goToQuote = () => {
        navigate("/", { state: { scrollTo: "request-quote" } })
    }
    
    return (
        <PortfolioPageContainer>
            <Navigation />

            <Header>
                <HeaderText>My Projects</HeaderText>
                <HeaderSubtext>
                    A few of the published projects that I have worked on, utilized for both personal and professional use
                </HeaderSubtext>
            </Header>

            <BodyContainer>
                {projects.map((project) => (
                    <ProjectItemContainer key={project.title}>
                        <BodyTextHeader>{project.title}</BodyTextHeader>
                        <BodyText>{project.description}</BodyText>

                        {(statusLabel[project.status] || project.url) && (
                            <ProjectMeta>
                                {statusLabel[project.status] && (
                                    <StatusTag $tone={project.status}>
                                        {statusLabel[project.status]}
                                    </StatusTag>
                                )}
                                {project.url && (
                                    <ProjectLink
                                        href={project.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Visit site
                                    </ProjectLink>
                                )}
                            </ProjectMeta>
                        )}
                    </ProjectItemContainer>
                ))}
                <QuoteButton onClick={goToQuote}>Request a Quote</QuoteButton>
            </BodyContainer>

            <Footer />
        </PortfolioPageContainer>
    )
}

const PortfolioPageContainer = styled.div`
    font-family: "Inter";
    background-color: #E3F2FD;
    color: #14212f;
    min-height: 100vh;
    overflow-x: hidden;
`

const Header = styled.header`
    max-width: 640px;
    margin: 0 auto;
    padding: clamp(2.5rem, 6vw, 4.5rem) 1.5rem 2.5rem;
    text-align: left;
`

const HeaderText = styled.h1`
    font-size: clamp(1.75rem, 4vw, 2.5rem);
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: -0.02em;
    margin: 0 0 0.75rem;
    color: #0d47a1;
`

const HeaderSubtext = styled.p`
    font-size: 1.0625rem;
    line-height: 1.6;
    color: #4c5f74;
    margin: 0;
    max-width: 52ch;
`

const BodyContainer = styled.section`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    max-width: 720px;
    margin: 0 auto;
    padding: 0 1.5rem 4rem;
`

const QuoteButton = styled.button`
    margin: auto;
    margin-top: 1.5em;
    width: min(200px, 100%);
    padding: 10px 20px;
    background-color: #2196f3;
    color: #e3f2fd;
    border: none;
    border-radius: 4px;
    font-family: inherit;
    font-size: 16px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: #1976d2;
    }
`

const ProjectItemContainer = styled.article`
    background: #0d47a1;
    border: 1px solid #2196f3;
    border-radius: 18px;
    padding: clamp(1.25rem, 4vw, 1.75rem);
    color: #e3f2fd;
    box-shadow: 0 12px 30px -14px rgba(13, 71, 161, 0.5);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    @media (hover: hover) {
        &:hover {
            transform: translateY(-4px);
            box-shadow: 0 20px 40px -16px rgba(13, 71, 161, 0.6);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;
        &:hover {
            transform: none;
        }
    }
`

const BodyTextHeader = styled.h2`
    font-size: 1.25rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    margin: 0 0 0.75rem;
    color: #ffffff;
`

const BodyText = styled.p`
    font-size: 0.95rem;
    line-height: 1.6;
    color: #90caf9;
    margin: 0;
`

const ProjectMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    margin-top: 1.25rem;
    padding-top: 1.25rem;
    border-top: 1px solid rgba(144, 202, 249, 0.3);
`

const StatusTag = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8rem;
    color: #e3f2fd;

    &::before {
        content: "";
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #90caf9;
    }
`

const ProjectLink = styled.a`
    font-size: 0.875rem;
    font-weight: 600;
    color: #ffffff;
    text-decoration: none;
    border-bottom: 1px solid #90caf9;
    padding-bottom: 1px;
    transition: opacity 0.15s ease;

    &:hover {
        opacity: 0.8;
    }

    &:focus-visible {
        outline: 2px solid #90caf9;
        outline-offset: 3px;
        border-radius: 2px;
    }
`

export default PortfolioPage